class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.dialogManager = null;
    this.currentItemCount = 0;
  }

  connectedCallback() {
    // Ensure DOM is ready before initializing
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.waitForDialogManager());
    } else {
      this.waitForDialogManager();
    }
  }

  waitForDialogManager() {
    // Wait for DialogManager to be available
    if (window.DialogManager) {
      this.init();
    } else {
      // If DialogManager isn't loaded yet, wait for it
      const checkInterval = setInterval(() => {
        if (window.DialogManager) {
          clearInterval(checkInterval);
          this.init();
        }
      }, 50);
    }
  }

  init() {
    const cartButton = document.querySelector('#header-basket');
    const overlay = document.getElementById('CartDrawer-Overlay');
    const dialogElement = this.querySelector('#CartDrawer-Dialog');

    if (!cartButton || !dialogElement) {
      console.error('CartDrawer: Missing required elements');
      return;
    }

    // Create DialogManager instance - pass dialogElement, not this
    this.dialogManager = new DialogManager(dialogElement, {
      type: 'drawer-right',
      triggerElement: cartButton,
      overlayElement: overlay,
      closeElements: Array.from(
        this.querySelectorAll('.cart-drawer__close, #CartDrawer-Close, #CartDrawer-CloseEmpty, #cart-drawer-close')
      ),
      onOpen: () => {
        this.setAttribute('aria-hidden', 'false');
        // Close header drawer if it's open
        const headerDrawer = document.querySelector('header-drawer');
        if (headerDrawer && headerDrawer.dialogManager) {
          headerDrawer.dialogManager.close();
        }

        // Set cart icon background to yellow when drawer is open
        cartButton.style.backgroundColor = '#ffd90b';

        // Handle cart note summary accessibility
        const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
        if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
          this.setSummaryAccessibility(cartDrawerNote);
        }
      },
      onClose: () => {
        this.setAttribute('aria-hidden', 'true');
        // Remove yellow background from cart icon when drawer is closed
        cartButton.style.backgroundColor = '';
      },
      focusFirstElement: false, // We'll handle focus manually based on cart state
    });

    // Set aria-label for accessibility
    this.setAttribute('aria-label', 'Cart');

    // Expose methods for external use
    this.enhancedOpen = () => this.dialogManager?.open();
    this.enhancedClose = () => this.dialogManager?.close();

    // Override the open method to handle focus based on cart state
    const originalOpen = this.dialogManager.open.bind(this.dialogManager);
    this.dialogManager.open = () => {
      originalOpen();

      // Handle focus based on cart state
      const isCartEmpty = this.currentItemCount === 0;

      setTimeout(() => {
        if (isCartEmpty) {
          // Focus on continue shopping button for empty cart
          const continueButton = this.querySelector('.cart-continue-shopping');
          if (continueButton) {
            continueButton.focus();
          }
        } else {
          // Focus on first cart item for populated cart
          const firstCartItem = this.querySelector('[id^="CartItem-"]');
          if (firstCartItem) {
            const firstFocusable = firstCartItem.querySelector('a, button, input, select, textarea');
            if (firstFocusable) {
              firstFocusable.focus();
            }
          }
        }
      }, 100);
    };
  }

  // Legacy method for compatibility
  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.dialogManager?.open();
  }

  // Legacy method for compatibility
  close() {
    this.dialogManager?.close();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const cartItems = document.getElementById('CartDrawer-CartItems');
    if (cartItems && cartItems.classList.contains('is-empty')) {
      cartItems.classList.remove('is-empty');
    }
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // Update the cart state
    this.currentItemCount = parsedState.item_count || 0;
    this.classList.toggle('is-empty', this.currentItemCount === 0);

    setTimeout(() => {
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer-CartItems',
        section: 'cart-drawer',
        selector: '#CartDrawer-CartItems',
      },
      {
        id: 'header-basket',
        section: 'header-basket',
        selector: '.shopify-section',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  constructor() {
    super();

    // Global focus restoration system for cart drawer
    this.pendingFocusRestore = null;
    this.setupGlobalFocusRestoration();

    // Listen for cart updates from product forms (when items are added)
    document.addEventListener('cart:updated', (event) => {
      // Skip updates that originate from this cart drawer to prevent double updates
      if (event.detail?.source === 'cart-drawer') {
        return;
      }
      this.handleCartUpdate(event.detail);
    });

    // Initialize shipping calculator
    this.initShippingCalculator();
  }

  setupGlobalFocusRestoration() {
    // Store focus before any potential DOM changes
    const storeFocusBeforeUpdate = () => {
      const activeElement = document.activeElement;
      if (!activeElement) return;

      // Check if focused element is within this cart drawer
      if (!this.contains(activeElement)) return;

      // Store focus information
      if (activeElement.classList.contains('js-quantity-decrease')) {
        this.pendingFocusRestore = { type: 'decrease-button', line: activeElement.dataset.line };
      } else if (activeElement.classList.contains('js-quantity-increase')) {
        this.pendingFocusRestore = { type: 'increase-button', line: activeElement.dataset.line };
      } else if (activeElement.id?.match(/^Quantity-(\d+)$/)) {
        const match = activeElement.id.match(/^Quantity-(\d+)$/);
        this.pendingFocusRestore = { type: 'quantity-input', line: match[1] };
      }
    };

    // Restore focus after DOM changes
    const restoreFocusAfterUpdate = (clickId) => {
      if (!this.pendingFocusRestore || this.pendingFocusRestore.clickId !== clickId) return;

      setTimeout(() => {
        if (!this.pendingFocusRestore || this.pendingFocusRestore.clickId !== clickId) return;

        let elementToFocus = null;

        switch (this.pendingFocusRestore.type) {
          case 'decrease-button':
            elementToFocus = this.querySelector(`.js-quantity-decrease[data-line="${this.pendingFocusRestore.line}"]`);
            break;
          case 'increase-button':
            elementToFocus = this.querySelector(`.js-quantity-increase[data-line="${this.pendingFocusRestore.line}"]`);
            break;
          case 'quantity-input':
            elementToFocus = document.getElementById(`Quantity-${this.pendingFocusRestore.line}`);
            break;
        }

        if (elementToFocus && typeof elementToFocus.focus === 'function') {
          elementToFocus.focus();

          // Reinitialize accessibility states
          this.initializeQuantityAccessibility();
        }

        // Don't clear pendingFocusRestore immediately - let the force restore attempts work
        setTimeout(() => {
          // Only clear if this is still the current click
          if (this.pendingFocusRestore && this.pendingFocusRestore.clickId === clickId) {
            this.pendingFocusRestore = null;
          }
        }, 2000);
      }, 50);
    };

    // Hook into quantity button clicks to store focus
    this.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest('.js-quantity-decrease, .js-quantity-increase');
        if (button) {
          // Create unique ID for this click to prevent cleanup conflicts
          const clickId = Date.now() + '_' + Math.random().toString(36).substr(2, 9);

          storeFocusBeforeUpdate();

          // Store the click ID with the focus data
          if (this.pendingFocusRestore) {
            this.pendingFocusRestore.clickId = clickId;
          }

          // Set up a MutationObserver to watch for when buttons are re-enabled
          this.setupButtonReenabledWatcher(button.dataset.line, clickId);

          // Try multiple restoration attempts at different intervals
          // Early attempts (while loading)
          setTimeout(() => restoreFocusAfterUpdate(clickId), 100);
          setTimeout(() => this.forceRestoreFocus(clickId), 200);

          // Later attempts (after loading should be complete)
          setTimeout(() => this.forceRestoreFocus(clickId), 500);
          setTimeout(() => this.forceRestoreFocus(clickId), 800);
          setTimeout(() => this.forceRestoreFocus(clickId), 1200);
          setTimeout(() => this.forceRestoreFocus(clickId), 1600);
        }
      },
      true
    ); // Use capture phase to run before other handlers
  }

  forceRestoreFocus(clickId) {
    if (!this.pendingFocusRestore) return;

    if (this.pendingFocusRestore.clickId !== clickId) return;

    let elementToFocus = null;
    const line = this.pendingFocusRestore.line;

    switch (this.pendingFocusRestore.type) {
      case 'decrease-button':
        elementToFocus = this.querySelector(`.js-quantity-decrease[data-line="${line}"]`);
        break;
      case 'increase-button':
        elementToFocus = this.querySelector(`.js-quantity-increase[data-line="${line}"]`);
        break;
      case 'quantity-input':
        elementToFocus = document.getElementById(`Quantity-${line}`);
        break;
    }

    if (elementToFocus && typeof elementToFocus.focus === 'function') {
      // Check if the element is disabled due to loading state
      if (elementToFocus.disabled) {
        // If disabled due to loading, don't try alternatives - just wait for re-enable
        return;
      }

      // Check if the element is disabled due to min/max limits
      if (elementToFocus.getAttribute('aria-disabled') === 'true') {
        // Find an alternative focusable element in the same quantity control group
        const quantityGroup = elementToFocus.closest('.quantity');
        if (quantityGroup) {
          // Try the other quantity button first
          const alternativeButton =
            this.pendingFocusRestore.type === 'decrease-button'
              ? quantityGroup.querySelector('.js-quantity-increase')
              : quantityGroup.querySelector('.js-quantity-decrease');

          if (
            alternativeButton &&
            !alternativeButton.disabled &&
            alternativeButton.getAttribute('aria-disabled') !== 'true'
          ) {
            alternativeButton.focus();
            return;
          }

          // If no buttons are available, try to focus the quantity display area
          const quantityDisplay = quantityGroup.querySelector('[role="status"]');
          if (quantityDisplay) {
            quantityDisplay.setAttribute('tabindex', '0');
            quantityDisplay.focus();
            // Remove tabindex after focus to maintain normal tab order
            setTimeout(() => quantityDisplay.removeAttribute('tabindex'), 100);
            return;
          }
        }
        return;
      }

      if (document.activeElement !== elementToFocus) {
        elementToFocus.focus();
      }
    }
  }

  setupButtonReenabledWatcher(line, clickId) {
    if (!this.pendingFocusRestore) return;

    const targetButton = this.querySelector(`[data-line="${line}"]`);
    if (!targetButton) return;

    // Create a MutationObserver to watch for disabled attribute changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'disabled') {
          const target = mutation.target;
          // If button is re-enabled and it's our target button
          if (!target.disabled && target.dataset.line === line) {
            // Try to restore focus immediately
            this.forceRestoreFocus(clickId);
            // Disconnect observer after first successful restoration attempt
            observer.disconnect();
          }
        }
      });
    });

    // Observe all buttons for this line
    const buttonsForLine = this.querySelectorAll(`[data-line="${line}"]`);
    buttonsForLine.forEach((button) => {
      observer.observe(button, { attributes: true, attributeFilter: ['disabled'] });
    });

    // Clean up observer after 3 seconds to prevent memory leaks
    setTimeout(() => observer.disconnect(), 3000);
  }

  // Initialize shipping cost calculator
  initShippingCalculator() {
    // Wait for the global shipping calculator to be available
    const initWithCalculator = () => {
      if (window.shippingCalculator) {
        // Subscribe to shipping updates
        this.shippingUnsubscriber = window.shippingCalculator.subscribe((shippingData) => {
          this.displayShippingCost(shippingData);
        });

        // Trigger initial calculation
        window.shippingCalculator.calculateShipping();

        // Listen for country changes from the localization system
        document.addEventListener('localization:country-changed', (event) => {
          if (event.detail && event.detail.countryCode) {
            window.shippingCalculator.calculateShipping(null, event.detail.countryCode);
          }
        });
      } else {
        // Fallback to original implementation
        const currentCountry = this.getCurrentCountry();
        if (currentCountry) {
          this.calculateShippingCost(currentCountry);
        }

        // Listen for country changes from the localization system
        document.addEventListener('localization:country-changed', (event) => {
          if (event.detail && event.detail.countryCode) {
            this.calculateShippingCost(event.detail.countryCode);
          }
        });
      }
    };

    // Check if shipping calculator is already available
    if (window.shippingCalculator) {
      initWithCalculator();
    } else {
      // Wait for shipping calculator to load
      let checkCount = 0;
      const checkInterval = setInterval(() => {
        checkCount++;
        if (window.shippingCalculator || checkCount > 50) {
          // Stop after 5 seconds
          clearInterval(checkInterval);
          initWithCalculator();
        }
      }, 100);
    }
  }

  // Get the current selected country from the localization system
  getCurrentCountry() {
    // Try to get from the localization form if it exists
    const localizationForm = document.querySelector('localization-form');
    if (localizationForm) {
      const countryInput = localizationForm.querySelector('input[name="country_code"]');
      if (countryInput && countryInput.value) {
        return countryInput.value;
      }
    }

    // Fallback: try to get from any country localization button
    const countryButton = document.querySelector(
      '[data-country-localization] button[name="country_code"][aria-current="true"]'
    );
    if (countryButton) {
      return countryButton.value;
    }

    // Final fallback: use the server-side localization country (embedded in template)
    const shippingSection = document.getElementById('CartDrawer-shipping-cost-section');
    if (shippingSection) {
      const countryCode = shippingSection.dataset.countryCode;
      return countryCode;
    }

    return null;
  }

  // Calculate shipping cost for selected country
  async calculateShippingCost(countryCode) {
    if (!countryCode) {
      this.displayShippingCost(null);
      return;
    }

    const loadingElement = document.getElementById('CartDrawer-shipping-cost-loading');
    const amountElement = document.getElementById('CartDrawer-shipping-cost-amount');
    const methodNameElement = document.getElementById('CartDrawer-shipping-method-name');

    // Show loading state
    if (loadingElement) loadingElement.classList.remove('hidden');
    if (amountElement) amountElement.textContent = '';

    try {
      // Get cart data
      const cartResponse = await fetch('/cart.js');
      const cartData = await cartResponse.json();

      if (cartData.item_count === 0) {
        this.displayShippingCost(null);
        return;
      }

      // Prepare shipping rate request with default postcodes for countries that require them
      const defaultPostcodes = {
        GB: 'SW1A 1AA', // UK - Westminster, London
        US: '10001', // US - New York
        CA: 'K1A 0A6', // Canada - Ottawa
        AU: '2000', // Australia - Sydney
        DE: '10115', // Germany - Berlin
        FR: '75001', // France - Paris
        IT: '00118', // Italy - Rome
        ES: '28001', // Spain - Madrid
        NL: '1012', // Netherlands - Amsterdam
        BE: '1000', // Belgium - Brussels
        CH: '3001', // Switzerland - Bern
        AT: '1010', // Austria - Vienna
        SE: '11120', // Sweden - Stockholm
        NO: '0150', // Norway - Oslo
        DK: '1050', // Denmark - Copenhagen
        FI: '00100', // Finland - Helsinki
        IE: 'D01', // Ireland - Dublin
        PT: '1000-001', // Portugal - Lisbon
        PL: '00-001', // Poland - Warsaw
        CZ: '110 00', // Czech Republic - Prague
        HU: '1011', // Hungary - Budapest
        GR: '10431', // Greece - Athens
        JP: '100-0001', // Japan - Tokyo
        KR: '04524', // South Korea - Seoul
        SG: '018956', // Singapore
        HK: '999077', // Hong Kong
        NZ: '6011', // New Zealand - Wellington
        ZA: '8001', // South Africa - Cape Town
        BR: '01310-100', // Brazil - São Paulo
        MX: '06600', // Mexico - Mexico City
        AR: 'C1002', // Argentina - Buenos Aires
        CL: '8320000', // Chile - Santiago
        CO: '110111', // Colombia - Bogotá
        PE: '15001', // Peru - Lima
        IN: '110001', // India - New Delhi
        CN: '100000', // China - Beijing
        TH: '10100', // Thailand - Bangkok
        MY: '50000', // Malaysia - Kuala Lumpur
        PH: '1000', // Philippines - Manila
        ID: '10110', // Indonesia - Jakarta
        VN: '100000', // Vietnam - Hanoi
        TR: '34000', // Turkey - Istanbul
        RU: '101000', // Russia - Moscow
        UA: '01001', // Ukraine - Kyiv
        IL: '9100001', // Israel - Jerusalem
        AE: '00000', // UAE - Dubai
        SA: '11564', // Saudi Arabia - Riyadh
        EG: '11511', // Egypt - Cairo
        MA: '10000', // Morocco - Rabat
        NG: '100001', // Nigeria - Lagos
        KE: '00100', // Kenya - Nairobi
        GH: '00233', // Ghana - Accra
      };

      const shippingAddress = {
        country: countryCode,
        province: '',
        zip: defaultPostcodes[countryCode] || '',
        city: '',
        address1: '',
        address2: '',
      };

      // Build query string for shipping rates
      const params = new URLSearchParams({
        'shipping_address[country]': shippingAddress.country,
        'shipping_address[province]': shippingAddress.province,
        'shipping_address[zip]': shippingAddress.zip,
        'shipping_address[city]': shippingAddress.city,
        'shipping_address[address1]': shippingAddress.address1,
        'shipping_address[address2]': shippingAddress.address2,
      });

      // Add cart items to the request
      cartData.items.forEach((item, index) => {
        params.append(`items[${index}][variant_id]`, item.variant_id);
        params.append(`items[${index}][quantity]`, item.quantity);
      });

      const shippingUrl = `/cart/shipping_rates.json?${params.toString()}`;

      // Fetch shipping rates
      const shippingResponse = await fetch(shippingUrl);

      if (shippingResponse.ok) {
        const shippingData = await shippingResponse.json();
        this.displayShippingCost(shippingData.shipping_rates, countryCode, defaultPostcodes[countryCode]);
      } else {
        const errorText = await shippingResponse.text();

        // Try to parse the error to see if it's a postcode requirement
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.zip && errorData.zip.includes('postcode')) {
            this.displayShippingCost('postcode_required');
          } else {
            this.displayShippingCost('unavailable');
          }
        } catch (e) {
          this.displayShippingCost('unavailable');
        }
      }
    } catch (error) {
      this.displayShippingCost('error');
    } finally {
      // Hide loading state
      if (loadingElement) loadingElement.classList.add('hidden');
    }
  }

  // Display shipping cost in the UI (updated to handle shared shipping calculator data)
  displayShippingCost(shippingData) {
    const amountElement = document.getElementById('CartDrawer-shipping-cost-amount');
    const methodNameElement = document.getElementById('CartDrawer-shipping-method-name');

    if (!amountElement) {
      return;
    }

    let shippingCostInPence = 0; // Track shipping cost for total calculation

    // Handle new shipping data structure from shared calculator
    if (shippingData && typeof shippingData === 'object') {
      if (shippingData.status === 'empty') {
        amountElement.textContent = '—';
        if (methodNameElement) methodNameElement.textContent = 'Shipping Estimate';
        this.updateCartTotal(0);
        return;
      }

      if (shippingData.status === 'unavailable' || shippingData.status === 'postcode_required') {
        amountElement.textContent = 'Calculated at checkout';
        if (methodNameElement) methodNameElement.textContent = 'Shipping Estimate';
        this.updateCartTotal(null);
        return;
      }

      if (shippingData.status === 'error') {
        amountElement.textContent = 'Unable to calculate';
        if (methodNameElement) methodNameElement.textContent = 'Shipping Estimate';
        this.updateCartTotal(null);
        return;
      }

      if (shippingData.status === 'available' && shippingData.cheapestRate) {
        const cheapestRate = shippingData.cheapestRate;
        shippingCostInPence = shippingData.shippingCost || 0;

        // Update the shipping method name
        if (methodNameElement) {
          methodNameElement.textContent = 'Shipping Estimate';
        }

        // Format and display the price
        if (shippingData.isFreeShipping) {
          amountElement.textContent = 'Free';
        } else if (window.shippingCalculator) {
          const formattedPrice = window.shippingCalculator.formatCurrency(shippingCostInPence, shippingData.country);
          amountElement.textContent = formattedPrice;
        } else {
          // Fallback formatting
          const formattedPrice = new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
          }).format(shippingCostInPence / 100);
          amountElement.textContent = formattedPrice;
        }

        this.updateCartTotal(shippingCostInPence);
        return;
      }
    }

    // Fallback for legacy format or unknown data
    this.displayShippingCostLegacy(shippingData);
  }

  // Legacy display method for backward compatibility
  displayShippingCostLegacy(shippingRates, countryCode, defaultPostcode) {
    const amountElement = document.getElementById('CartDrawer-shipping-cost-amount');
    const methodNameElement = document.getElementById('CartDrawer-shipping-method-name');

    if (!amountElement) {
      return;
    }

    let shippingCostInPence = 0;

    if (!shippingRates) {
      amountElement.textContent = '—';
      if (methodNameElement) methodNameElement.textContent = 'Shipping Estimate';
      this.updateCartTotal(shippingCostInPence);
      return;
    }

    if (shippingRates === 'unavailable') {
      amountElement.textContent = 'Calculated at checkout';
      if (methodNameElement) methodNameElement.textContent = 'Shipping Estimate';
      this.updateCartTotal(null);
      return;
    }

    if (shippingRates === 'error') {
      amountElement.textContent = 'Unable to calculate';
      if (methodNameElement) methodNameElement.textContent = 'Shipping Estimate';
      this.updateCartTotal(null);
      return;
    }

    if (shippingRates === 'postcode_required') {
      amountElement.textContent = 'Calculated at checkout';
      if (methodNameElement) methodNameElement.textContent = 'Shipping Estimate';
      this.updateCartTotal(null);
      return;
    }

    if (Array.isArray(shippingRates) && shippingRates.length > 0) {
      const cheapestRate = shippingRates.reduce((min, rate) =>
        parseFloat(rate.price) < parseFloat(min.price) ? rate : min
      );

      shippingCostInPence = Math.round(parseFloat(cheapestRate.price) * 100);

      if (methodNameElement) {
        methodNameElement.textContent = 'Shipping Estimate';
      }

      const formattedPrice = new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
      }).format(parseFloat(cheapestRate.price));

      if (parseFloat(cheapestRate.price) === 0) {
        amountElement.textContent = 'Free';
        shippingCostInPence = 0;
      } else {
        if (defaultPostcode) {
          amountElement.textContent = `${formattedPrice}`;
          amountElement.title = `Estimated shipping cost using ${defaultPostcode}. Final cost calculated at checkout.`;
        } else {
          amountElement.textContent = formattedPrice;
        }
      }

      this.updateCartTotal(shippingCostInPence);
    } else {
      amountElement.textContent = 'Calculated at checkout';
      if (methodNameElement) methodNameElement.textContent = 'Shipping Estimate';
      this.updateCartTotal(null);
    }
  }

  // Update the cart total to include shipping costs
  updateCartTotal(shippingCostInPence) {
    const totalElement = document.getElementById('CartDrawer-cart-total-amount');
    if (!totalElement) return;

    const cartSubtotalInPence = parseInt(totalElement.dataset.subtotal);

    if (shippingCostInPence === null) {
      // If shipping is unavailable/error, show just the cart subtotal
      const formattedSubtotal = new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
      }).format(cartSubtotalInPence / 100);
      totalElement.textContent = formattedSubtotal;
    } else {
      // Add shipping to cart subtotal
      const totalInPence = cartSubtotalInPence + shippingCostInPence;
      const formattedTotal = new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
      }).format(totalInPence / 100);
      totalElement.textContent = formattedTotal;
    }
  }

  async handleCartUpdate(cartData) {
    // Check if cart drawer is currently showing empty state
    const emptyState = document.getElementById('CartDrawer-EmptyState');
    const cartDrawerItems = document.querySelector('cart-drawer-items');

    // Cart is empty if we have the empty state element visible OR no cart-drawer-items element
    const isCurrentlyEmpty = (emptyState && emptyState.offsetParent !== null) || !cartDrawerItems;

    // If cart was empty and now has items, refresh the drawer (but don't auto-open)
    if (isCurrentlyEmpty && cartData && cartData.item_count > 0) {
      await this.refreshFromEmptyToFilled();
    } else if (!isCurrentlyEmpty && cartData && cartData.item_count > 0) {
      // Cart already has items, just refresh the cart drawer items
      this.onCartUpdate();
    }

    // ONLY recalculate shipping if this is the primary cart update handler
    // This prevents multiple simultaneous shipping calculations
    if (window.shippingCalculator) {
      // Clear cache to ensure fresh calculation
      window.shippingCalculator.clearCache();
      // Use shared shipping calculator - this will notify all subscribers
      window.shippingCalculator.calculateShipping(cartData);
    } else {
      // Fallback to legacy implementation
      const currentCountry = this.getCurrentCountry();
      if (currentCountry) {
        this.calculateShippingCost(currentCountry);
      }
    }
  }

  refreshFromEmptyToFilled() {
    // Add a small delay to ensure server-side cart state is updated
    return new Promise((resolve) => {
      setTimeout(() => {
        // Force a fresh fetch to get the cart with items
        const timestamp = Date.now();
        fetch(`${routes.cart_url}?section_id=cart-drawer&t=${timestamp}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');

            // Get the updated cart-drawer-items content
            const sourceCartDrawerItems = html.querySelector('cart-drawer-items');
            const targetCartDrawerItems = document.querySelector('cart-drawer-items');
            const emptyState = document.getElementById('CartDrawer-EmptyState');

            if (sourceCartDrawerItems && targetCartDrawerItems) {
              // Replace the cart-drawer-items content
              targetCartDrawerItems.replaceWith(sourceCartDrawerItems);

              // Hide empty state and show cart items
              if (emptyState) {
                emptyState.classList.add('hidden');
              }
              sourceCartDrawerItems.classList.remove('hidden');
            }

            resolve();
          })
          .catch((e) => {
            console.error('Error refreshing cart from empty to filled:', e);
            resolve(); // Still resolve to prevent hanging
          });
      }, 100); // Small delay to ensure server state is updated
    });
  }

  onCartUpdate() {
    return fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const selectors = ['cart-drawer-items'];
        for (const selector of selectors) {
          const targetElement = document.querySelector(selector);
          const sourceElement = html.querySelector(selector);
          if (targetElement && sourceElement) {
            targetElement.replaceWith(sourceElement);
          }
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);

    // Store focus information before DOM update
    const focusInfo = this.storeFocusState(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: ['cart-drawer'],
      sections_url: window.location.pathname,
    });
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        if (parsedState.errors) {
          const quantityElement = document.getElementById(`Quantity-${line}`);
          if (quantityElement) {
            quantityElement.value = quantityElement.getAttribute('value');
          }
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        // If cart becomes empty, do a full refresh to get proper server-side empty state
        if (parsedState.item_count === 0) {
          this.refreshEmptyCart();
          return;
        }

        // Check if any item has discount changes that would require a full refresh
        const needsFullRefresh = this.hasDiscountChanges(parsedState);

        if (needsFullRefresh) {
          // Do a full cart refresh to ensure discount information is properly updated
          this.onCartUpdate()
            .then(() => {
              // Restore focus after full refresh
              this.restoreFocusState(focusInfo, parsedState);
              // Reinitialize accessibility states after full refresh
              this.initializeQuantityAccessibility();

              // Update header basket count manually
              this.updateHeaderBasketCount(parsedState.item_count);

              // Dispatch cart:updated event for promo banner and other components
              document.dispatchEvent(
                new CustomEvent('cart:updated', {
                  detail: {
                    ...parsedState,
                    source: 'cart-drawer',
                  },
                })
              );

              this.updateLiveRegions(line, '');

              // Small delay to ensure DOM updates are fully rendered before disabling loading
              setTimeout(() => {
                this.disableLoading(line);
              }, 50);
            })
            .catch(() => {
              this.updateLiveRegions(line, window.cartStrings.error);
              // Disable loading state on error
              this.disableLoading(line);
            });
          return;
        }

        // Update only the changed elements without replacing images
        this.updateCartSelectively(parsedState, line);

        // Restore focus after selective update
        this.restoreFocusState(focusInfo, parsedState);

        // Reinitialize accessibility states after selective update
        this.initializeQuantityAccessibility();

        // Update header basket count manually
        this.updateHeaderBasketCount(parsedState.item_count);

        // Dispatch cart:updated event for promo banner and other components
        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: {
              ...parsedState,
              source: 'cart-drawer',
            },
          })
        );

        this.updateLiveRegions(line, '');
      })
      .catch((error) => {
        const errors = document.getElementById('CartDrawer-CartErrors');
        if (errors) {
          errors.textContent = window.cartStrings.error;
        }
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  hasDiscountChanges(parsedState) {
    // Store current discount state before the change
    const currentDiscountStates = new Map();

    // Get current discount states from the DOM
    document.querySelectorAll('[id^="CartDrawer-Item-"]').forEach((item) => {
      const lineMatch = item.id.match(/CartDrawer-Item-(\d+)/);
      if (lineMatch) {
        const line = parseInt(lineMatch[1]);
        const hasDiscount = item.querySelector('[data-discount-section="true"]') !== null;
        const hasStrikethrough = item.querySelector('.line-through') !== null;
        currentDiscountStates.set(line, hasDiscount || hasStrikethrough);
      }
    });

    // Check if any items have discount-related changes
    let hasChanges = false;

    parsedState.items.forEach((item, index) => {
      const line = index + 1;
      const hasDiscountNow =
        item.original_price !== item.final_price ||
        (item.variant && item.variant.compare_at_price && item.variant.compare_at_price > item.final_price);

      const hadDiscountBefore = currentDiscountStates.get(line) || false;

      // If discount state changed, we need a full refresh
      if (hasDiscountNow !== hadDiscountBefore) {
        hasChanges = true;
      }
    });

    return hasChanges;
  }

  updateCartSelectively(parsedState, changedLine) {
    // Check if the item still exists in the cart
    const quantityInput = document.querySelector(`#Quantity-${changedLine}`);
    const currentQuantity = quantityInput ? parseInt(quantityInput.value) : 0;

    // If quantity is 0, remove the item immediately
    if (currentQuantity === 0) {
      const itemElement = document.getElementById(`CartDrawer-Item-${changedLine}`);
      if (itemElement) {
        itemElement.remove();
      }

      // Update line numbers for all remaining items
      this.updateLineNumbers(parsedState);
    } else {
      // Update the quantity display and underlying input for the changed item
      const quantityInputEl = document.querySelector(`#Quantity-${changedLine}`);
      const quantityDisplay = quantityInputEl?.parentNode.querySelector('.quantity-display');

      // Find the updated item in the cart (items may have shifted indices)
      const variantId = quantityInput?.getAttribute('data-quantity-variant-id');
      const updatedItem = parsedState.items.find((item) => item.variant_id.toString() === variantId);

      if (updatedItem) {
        // Sync hidden input value so subsequent clicks compute from confirmed state
        if (quantityInputEl) {
          quantityInputEl.value = updatedItem.quantity;
          quantityInputEl.setAttribute('value', updatedItem.quantity);
        }

        // Update visible display
        if (quantityDisplay) {
          quantityDisplay.textContent = updatedItem.quantity;
        }

        // Update Pair/Pairs text
        const pairText = quantityDisplay.parentNode.querySelector('[data-single][data-multiple]');
        if (pairText) {
          pairText.textContent =
            updatedItem.quantity > 1 ? pairText.getAttribute('data-multiple') : pairText.getAttribute('data-single');
        }

        // Update line item price
        const lineItemPrice = document.querySelector(
          `#CartDrawer-Item-${changedLine} .line-item-price[data-line-item-index="${changedLine}"]`
        );
        if (lineItemPrice) {
          // Calculate the total price using per-unit final price * quantity
          const finalTotalPrice = updatedItem.final_price * updatedItem.quantity;

          // Format the price properly
          const formattedPrice = new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
          }).format(finalTotalPrice / 100);
          lineItemPrice.textContent = formattedPrice;

          // Update the compare at price if it exists (for discounted items)
          const originalPriceSpan = lineItemPrice.parentNode.querySelector('.line-through');
          const compareAtPrice = lineItemPrice.getAttribute('data-compare-at-price');

          if (originalPriceSpan && compareAtPrice) {
            const compareAtTotalPrice = parseFloat(compareAtPrice) * updatedItem.quantity;
            const formattedCompareAtPrice = new Intl.NumberFormat('en-GB', {
              style: 'currency',
              currency: 'GBP',
            }).format(compareAtTotalPrice / 100);
            originalPriceSpan.textContent = formattedCompareAtPrice;
          }
        }

        // Update button states based on new quantity
        this.updateQuantityButtonStates(changedLine, updatedItem.quantity);
      }
    }

    // Update cart footer totals without replacing the entire footer
    const subtotalElement = document.querySelector(
      'cart-drawer-items .bg-white.w-full.p-4 .flex.items-center.justify-between p'
    );

    if (subtotalElement) {
      const formattedTotal = new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
      }).format(parsedState.total_price / 100);
      subtotalElement.textContent = formattedTotal;
    }

    // Update the order total (final total including shipping)
    const orderTotalElement = document.getElementById('CartDrawer-cart-total-amount');
    if (orderTotalElement) {
      orderTotalElement.dataset.subtotal = parsedState.total_price;
      // For now, just update with subtotal - shipping will be recalculated separately
      const formattedOrderTotal = new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
      }).format(parsedState.total_price / 100);
      orderTotalElement.textContent = formattedOrderTotal;
    }

    // Update the cart total data attribute for shipping calculations (legacy support)
    const totalElement = document.getElementById('cart-total-amount');
    if (totalElement) {
      totalElement.dataset.subtotal = parsedState.total_price;
    }

    // Update cart drawer empty state
    const cartDrawerWrapper = document.querySelector('cart-drawer');
    if (cartDrawerWrapper) {
      cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
    }

    // Shipping calculation will be triggered by the main cart:updated event
    // No need to recalculate here to avoid multiple simultaneous calculations
  }

  updateLineNumbers(parsedState) {
    // Get all remaining cart items in the DOM
    const itemElements = document.querySelectorAll('[id^="CartDrawer-Item-"]');

    // Update each item's line number to match the new cart state
    parsedState.items.forEach((item, index) => {
      const newLineNumber = index + 1;
      const variantId = item.variant_id.toString();

      // Find the DOM element for this variant
      const itemElement = Array.from(itemElements).find((el) => {
        const quantityInput = el.querySelector('input[data-quantity-variant-id]');
        return quantityInput && quantityInput.getAttribute('data-quantity-variant-id') === variantId;
      });

      if (itemElement) {
        // Update the item container ID
        itemElement.id = `CartDrawer-Item-${newLineNumber}`;

        // Update the quantity input ID and data-index
        const quantityInput = itemElement.querySelector('input[type="number"]');
        if (quantityInput) {
          quantityInput.id = `Quantity-${newLineNumber}`;
          quantityInput.setAttribute('data-index', newLineNumber);
        }

        // Update the label for attribute
        const label = itemElement.querySelector('label[for^="Quantity-"]');
        if (label) {
          label.setAttribute('for', `Quantity-${newLineNumber}`);
        }

        // Update the line item price data attribute
        const lineItemPrice = itemElement.querySelector('.line-item-price');
        if (lineItemPrice) {
          lineItemPrice.setAttribute('data-line-item-index', newLineNumber);
        }
      }
    });
  }

  updateHeaderBasketCount(itemCount) {
    const headerBasket = document.getElementById('header-basket');
    if (!headerBasket) return;

    // Update cart count display
    const countSpan = headerBasket.querySelector('span.inline-block span[aria-hidden="true"]');
    if (countSpan) {
      const newCount = itemCount < 100 ? itemCount : '99+';
      countSpan.textContent = newCount;
    }

    // Update screen reader text
    const srSpan = headerBasket.querySelector('span.inline-block span.sr-only');
    if (srSpan) {
      // Use the same translation key as in the template
      const countText = itemCount === 1 ? `${itemCount} item` : `${itemCount} items`;
      srSpan.textContent = countText;
    }
  }

  refreshEmptyCart() {
    // Show empty state and hide cart items
    const emptyState = document.getElementById('CartDrawer-EmptyState');
    const cartDrawerItems = document.querySelector('cart-drawer-items');

    if (emptyState) {
      emptyState.classList.remove('hidden');
    }

    if (cartDrawerItems) {
      cartDrawerItems.classList.add('hidden');
    }
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Intercept third-party cart modifications (e.g. Selleasy upsell widget)
// and trigger a cart refresh when they successfully mutate the cart.
(function () {
  const CART_MUTATION_PATHS = ['/cart/add', '/cart/change', '/cart/update', '/cart/clear'];
  let refreshTimer = null;
  let lastCartUpdateEvent = 0;

  // Apps also POST to /cart/update just to write cart attributes, which leaves the
  // drawer unchanged. Track what the drawer shows so those don't force a re-render.
  // Must match the data-cart-signature format in snippets/cart-drawer.liquid.
  function cartSignature(cart) {
    return cart.items.map((item) => `${item.key}:${item.quantity},`).join('') + '|' + cart.total_price;
  }
  let lastCartSignature = document.querySelector('cart-drawer')?.dataset.cartSignature ?? null;

  document.addEventListener('cart:updated', (event) => {
    lastCartUpdateEvent = Date.now();
    const cart = event.detail?.items ? event.detail : event.detail?.cartData;
    if (cart?.items) lastCartSignature = cartSignature(cart);
  });

  function isCartMutation(url) {
    try {
      const pathname = new URL(url, window.location.origin).pathname;
      return CART_MUTATION_PATHS.some((p) => pathname.startsWith(p));
    } catch {
      return CART_MUTATION_PATHS.some((p) => url.includes(p));
    }
  }

  function scheduleCartRefresh() {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      refreshTimer = null;
      if (Date.now() - lastCartUpdateEvent < 800) return;
      fetch('/cart.js')
        .then((r) => r.json())
        .then((cartData) => {
          if (cartSignature(cartData) === lastCartSignature) return;
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: cartData }));
        })
        .catch(() => {});
    }, 500);
  }

  const originalFetch = window.fetch;
  window.fetch = function (...args) {
    const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
    if (isCartMutation(url)) {
      return originalFetch.apply(this, args).then((response) => {
        if (response.ok) scheduleCartRefresh();
        return response;
      });
    }
    return originalFetch.apply(this, args);
  };

  const originalXHROpen = XMLHttpRequest.prototype.open;
  const originalXHRSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this._interceptedUrl = url;
    return originalXHROpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function (...args) {
    if (isCartMutation(this._interceptedUrl || '')) {
      this.addEventListener('load', function () {
        if (this.status >= 200 && this.status < 300) scheduleCartRefresh();
      });
    }
    return originalXHRSend.apply(this, args);
  };
})();
